import {
  ChatBound,
  ChatCitation,
  ChatCitationParagraph,
  ChatProvision,
} from '@dc-shared-web/providers'
import { SerializableHighlight } from '@dc-shared-web/lib/highlightSerializer'

const isChatProvision = (
  value: ChatCitationParagraph | ChatProvision
): value is ChatProvision => 'provision_id' in value

const isValidChatBound = (bound: unknown): bound is ChatBound => {
  if (!bound || typeof bound !== 'object') return false
  const b = bound as ChatBound
  return (
    typeof b.left === 'number' &&
    typeof b.top === 'number' &&
    typeof b.width === 'number' &&
    typeof b.height === 'number'
  )
}

const appendRectToMap = (
  rectsByPage: Map<number | undefined, ChatBound[]>,
  page: number | undefined,
  bound: unknown
) => {
  if (!isValidChatBound(bound)) return

  if (!rectsByPage.has(page)) {
    rectsByPage.set(page, [])
  }

  rectsByPage.get(page)!.push(bound)
}

const appendRectsByPage = (
  rectsByPage: Map<number | undefined, ChatBound[]>,
  value: ChatCitationParagraph | ChatProvision,
  page: number | undefined
) => {
  if (value.lines && value.lines.length > 0) {
    for (const line of value.lines) {
      appendRectToMap(rectsByPage, line.page, line.bounds)
    }
  } else if (value.bounds) {
    appendRectToMap(
      rectsByPage,
      isChatProvision(value) ? value.page : page,
      value.bounds
    )
  }
}

const getRectsByPage = (citation: ChatCitation) => {
  const rectsByPage = new Map<number | undefined, ChatBound[]>()

  // First try to process paragraphs
  for (const paragraph of citation.paragraphs) {
    appendRectsByPage(rectsByPage, paragraph, citation.page)
  }

  // If no rects were added, fall back to provisions
  if (rectsByPage.size === 0) {
    for (const provision of citation.provisions) {
      appendRectsByPage(rectsByPage, provision, citation.page)
    }
  }

  return rectsByPage
}

export const getCitationHighlights = (
  citation: ChatCitation
): SerializableHighlight[] => {
  const rectsByPage = getRectsByPage(citation)
  const result = []

  for (const [page, rects] of rectsByPage) {
    if (page) {
      result.push({
        page,
        rects,
      })
    }
  }

  return result
}
